import { A } from '@ember/array';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { Changeset } from 'ember-changeset';
import type { DetailedChangeset } from 'ember-changeset/types';
import { dropTask, type Task } from 'ember-concurrency';
import PowerSelectMultiple from 'ember-power-select/components/power-select-multiple';
import { not, or } from 'ember-truth-helpers';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import TaskButton from 'garaje/components/ui/classic/task-button';
import UiButton from 'garaje/components/ui-button';
import type ConnectLocationConfigurationModel from 'garaje/models/connect-location-configuration';
import type LocationModel from 'garaje/models/location';
import type UserModel from 'garaje/models/user';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST } from 'garaje/utils/roles';
import isEqual from 'lodash/isEqual';

interface ConnectNotificationsWalkInArgs {
  location: LocationModel;
  searchUsersTask: Task<UserModel[], [term: string, contacts: UserModel[]]>;
  connectLocationConfiguration: ConnectLocationConfigurationModel;
}

interface ConnectNotificationsWalkInSignature {
  Args: ConnectNotificationsWalkInArgs;
  Element: HTMLDivElement;
}

export default class ConnectNotificationsWalkIn extends Component<ConnectNotificationsWalkInSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare store: StoreService;

  @tracked isOpen = false;
  @tracked _contactIds: string[] = [];
  @tracked locationChangeset!: DetailedChangeset<LocationModel>;
  @tracked connectLocationConfigurationChangeset!: DetailedChangeset<ConnectLocationConfigurationModel>;

  get canEdit(): boolean {
    return (
      this.connectLocationConfigurationChangeset.connectWalkInApprovalContactsEnabled && !this.enableTask.isRunning
    );
  }

  get contacts(): UserModel[] {
    if (this.loadContactsTask.isRunning) return [];
    return this.locationChangeset.connectWalkInApprovalContacts.content
      ? this.locationChangeset.connectWalkInApprovalContacts.content
      : <UserModel[]>(<unknown>this.locationChangeset.connectWalkInApprovalContacts);
  }

  get contactsChanged(): boolean {
    return !isEqual(this._contactIds, A(this.contacts).mapBy('id'));
  }

  constructor(owner: unknown, args: ConnectNotificationsWalkInArgs) {
    super(owner, args);
    void this.loadContactsTask.perform(this.args.location);
  }

  enableTask = dropTask(async () => {
    if (!this.contacts.length) {
      this.isOpen = true;
      return;
    }

    await this.saveLocation(true);
    this.isOpen = true;
  });

  loadContactsTask = dropTask(async (location: LocationModel) => {
    try {
      this.connectLocationConfigurationChangeset = Changeset(this.args.connectLocationConfiguration);
      const users = await location.connectWalkInApprovalContacts;
      this._contactIds = users.map((user) => user.id);
      this.locationChangeset = Changeset(this.args.location);
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  searchTask = dropTask(async (term: string) => {
    const userRoles = await this.store.query('user-role', {
      filter: {
        name: term,
        location: this.args.location.id,
        roles: [GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST].join(),
        confirmedUsers: true,
      },
      include: 'user',
    });

    // deduplicate users, since a single user may have multiple matching roles and we only want to show them once
    const allUsers = await Promise.all(userRoles.map((role) => role.user));
    const uniqueUsers = new Set(allUsers);
    return Array.from(uniqueUsers);
  });

  saveTask = dropTask(async () => {
    await this.saveLocation(!!this.contacts.length);
    void this.loadContactsTask.perform(this.args.location);
  });

  @action
  async saveLocation(enabled: boolean): Promise<void> {
    try {
      const contactsChanged = !!A(this.locationChangeset.changes).findBy('key', 'connectWalkInApprovalContacts');
      if (isPresent(enabled)) {
        this.connectLocationConfigurationChangeset.connectWalkInApprovalContactsEnabled = enabled;
      }

      await this.connectLocationConfigurationChangeset.save();
      await this.locationChangeset.save();
      if (!enabled) {
        this.isOpen = false;
      }

      if (contactsChanged) {
        this.flashMessages.showAndHideFlash('success', 'Saved!');
      }
    } catch (e) {
      this.locationChangeset.rollback();
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  }

  @action
  cancel(): void {
    this.isOpen = false;
    this.locationChangeset.rollback();
    this.connectLocationConfigurationChangeset.rollback();
  }

  @action
  setContacts(users: UserModel[]): void {
    // Cast to work around Ember Data type constraints
    (
      (<unknown>this.locationChangeset) as { connectWalkInApprovalContacts: UserModel[] }
    ).connectWalkInApprovalContacts = users;
  }

  @action
  onEditButtonClicked(): void {
    this.isOpen = true;
  }

  @action
  async searchUsers(term: string): Promise<UserModel[]> {
    const result = await this.searchTask.perform(term);
    return result;
  }

  <template>
    <SettingsPanel data-test-connect-walk-in-notifications @isOpen={{this.isOpen}} ...attributes as |panel|>
      <panel.header
        @iconUri="/assets/images/features/notifications.svg"
        @title="Admin notifications for walk-in visitors requiring approval"
        @description="Get notified when connected accounts need approval to check in walk-in visitors."
        @showSecondaryDescription={{true}}
        @secondaryDescription="Note: Only global admins, location admins, and front desk admins can approve or deny walk-in visitors."
        @onCancel={{this.cancel}}
        as |header|
      >
        <header.controls>
          {{#if panel.isOpen}}
            <TaskButton
              class="btn btn--small btn--brand"
              @disabled={{or (not this.contactsChanged) this.loadContactsTask.isRunning}}
              @task={{this.saveTask}}
              @text="Save"
              @runningText="Saving..."
              data-test-save-connect-walk-in-notifications
            />
          {{else if this.canEdit}}
            <UiButton
              class="btn btn--small btn--brand"
              {{on "click" this.onEditButtonClicked}}
              data-test-edit-connect-walk-in-notifications
            >
              Edit
            </UiButton>
          {{else}}
            <TaskButton
              class="btn btn--small btn--brand"
              @task={{this.enableTask}}
              @disabled={{this.loadContactsTask.isRunning}}
              @text="Enable"
              @runningText="Enabling..."
              data-test-enable-connect-walk-in-notifications
            />
          {{/if}}
        </header.controls>
      </panel.header>

      <panel.body class="mt-8">
        <div class="mx-16" data-test-connect-walk-in-contacts-input>
          <label id="connect-walk-in-contacts">Connect notification contact(s)</label>

          <PowerSelectMultiple
            data-test-connect-walk-in-contacts-select
            class="w-10/12"
            @placeholder="Choose a contact"
            @ariaDescribedBy="connect-walk-in-contacts"
            @allowClear={{true}}
            @selected={{this.contacts}}
            @searchEnabled={{true}}
            @search={{this.searchUsers}}
            @onChange={{this.setContacts}}
            @disabled={{or this.loadContactsTask.isRunning this.saveTask.isRunning}}
            @searchField="fullName"
            as |user|
          >
            {{user.fullName}}
          </PowerSelectMultiple>
        </div>

        {{#if this.connectLocationConfigurationChangeset.connectWalkInApprovalContactsEnabled}}
          <div class="flex justify-end">
            <TaskButton
              data-test-connect-walk-in-notifications-disable-button
              class="btn btn--small btn--outlineFill"
              @text="Disable"
              @runningText="Disabling..."
              @task={{fn this.saveLocation false}}
              @disabled={{or this.saveTask.isRunning this.loadContactsTask.isRunning}}
            />
          </div>
        {{/if}}
      </panel.body>
    </SettingsPanel>
  </template>
}
